import { useEffect, useRef, useState } from "react";
import FileService from "../../../services/FileService";
import { TranslationService } from "../../../services/TranslationService";
import { ToastService } from "../../shared/bootstrap/Toast";
import CompanyService from "../../../services/CompanyService";
import { handleErrorWithToast } from "../../../utils/RequestUtils";
import AuthenticationService from "../../../services/AuthenticationService";
import { getCache } from "../../../utils/PersistenceUtils";
import { IAuthenticationData, AUTHENTICATION_DATA_STORAGE_KEY } from "../../shared/AuthenticationContext";
import Collapsable from "../../shared/bootstrap/Collapsable";
import UserService from "../../../services/UserService";
import Loading from "../../shared/components/Loading";

const DataCompanyConfiguration = () => {
    const [imgSrc, setImgSrc] = useState(CompanyService.getCompanyAuth()?.logoUrl);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const newFile = useRef<Promise<undefined | string> | undefined>();
    const newName = useRef<string>();
    const [saving, setSaving] = useState(false);
    const [showDeleteCompany, setShowDeleteCompany] = useState<boolean>();
    const [changingData, setChangingData] = useState(false);
    const [changingImgData, setChangingImgData] = useState(Boolean(imgSrc) ?? false);

    const handleChangeData = () => setChangingData(p => !p);

    const onFileChange = async (fileList: FileList | null) => {
        const uploadFile = async (fileList: FileList | null) => {
            const fileUpload = async (file: File) => {
                const result = await FileService.upload(file);
                if (result instanceof Error) {
                    ToastService.showToast(TranslationService.translate.ErrorProcessingRequest, undefined, "danger");
                    return result;
                }
                return { id: result.id, fileName: result.fileName };
            };
            if (!(fileList?.length)) {
                setImgSrc(undefined);
                newFile.current = Promise.resolve(undefined);
                return undefined;
            }
            const file = fileList[0];
            setImgSrc(URL.createObjectURL(file));
            const result = await fileUpload(file);
            if (result instanceof Error) {
                ToastService.showToast(TranslationService.translate.ErrorProcessingRequest, undefined, "danger");
                return undefined;
            }
            return result.id;
        };
        newFile.current = uploadFile(fileList);
        setChangingImgData(true)
    };

    useEffect(() => {
        const getData = async () => {
            const list = await UserService.getList({ page: 0, pagesize: 1000, text: null });
            if (!list || list instanceof Error) {
                setShowDeleteCompany(false);
                return;
            }
            const result = list.users.find(x => x.UserID.toString() === CompanyService.getUserid());
            if (!result) {
                setShowDeleteCompany(false);
                return;
            }
            
            if (CompanyService.getIsOwner() && CompanyService.canDo("deletecompany") && CompanyService.getCompanyToken()?.production === "0") {
                setShowDeleteCompany(true);
            } else {
                setShowDeleteCompany(false);
            }
        };
        getData();
    }, []);

    const onSave = async () => {
        setSaving(true);
        const saveName = newName.current ? handleErrorWithToast(CompanyService.changeCompanyName(newName.current)) : Promise.resolve();
        await saveName;
        await AuthenticationService.refreshToken({ refreshToken: getCache<IAuthenticationData>(AUTHENTICATION_DATA_STORAGE_KEY)!, refreshlists: 1 });
        window.location.reload();
        setSaving(false);
    };
    const onSaveImg = async () => {
        const fileId = await newFile.current;
        setSaving(true);
        const saveImage = newFile.current ? handleErrorWithToast(CompanyService.changeCompanyLogo((fileId ?? null) as string)) : Promise.resolve();
        await saveImage;
        await AuthenticationService.refreshToken({ refreshToken: getCache<IAuthenticationData>(AUTHENTICATION_DATA_STORAGE_KEY)!, refreshlists: 1 });
        window.location.reload();
        setSaving(false);
    };

    if (showDeleteCompany === undefined) {
        return <Loading height={238} />;
    }
    return (
        <div className="main-layout-container mx-3">
            <div className="main-panel">
                <h4 className="mb-2">{TranslationService.translate.Company}</h4>
                <div className="card">
                    <div className="card-body">

                        <div className='d-flex align-items-center show-child-on-hover gap-3 mb-2'>
                            <h5>{TranslationService.translate.CompanyName}</h5>
                            {!changingData ?
                                <i className="far fa-pen pointer display-when-hovering-parent" onClick={handleChangeData} ></i>
                                : <i className="fa-fw fa-solid fa-times text-danger pointer" onClick={handleChangeData} />}
                        </div>

                        <div className="col">
                            {changingData ?
                                <div className="d-flex justify-content-between align-items-center gap-3">
                                    <input type="text" className="form-control w-100" defaultValue={CompanyService.getCompanyName()} onChange={(x) => newName.current = x.target.value}></input>
                                    <div className='d-flex gap-2'>
                                        {saving ?
                                            <i className="fas fa-spinner-third fa-spin third ms-2" /> :
                                            <button className="col btn btn-primary" onClick={onSave}>{TranslationService.translate.Save}</button>}
                                    </div>
                                </div>
                                :
                                <div className="col mb-3">
                                    <p>{CompanyService.getCompanyName()}</p>
                                </div>}
                        </div>
                        <hr />

                        <div className='d-flex align-items-center justify-content-between mt-4 mb-3'>
                            <h5>{TranslationService.translate.CompanyLogo}</h5>
                        </div>

                        <input type="file" className="d-none" accept=".jpg,.jpeg,.png" ref={inputRef} onChange={(e) => { onFileChange(e.target.files); !changingImgData && setChangingImgData(true) }}></input>
                        {!imgSrc && <div className="row">
                            <div className="col">
                                <button className="btn btn-link px-0" type="button" onClick={() => inputRef.current?.click()}>{TranslationService.translate.UploadCompanysLogo}</button>
                            </div>
                        </div>}

                        {imgSrc &&
                            <div className="gap-3">
                                <div className="img-signature-container companyLogo">
                                    <img width={'100%'} height={'auto'} src={imgSrc} alt={TranslationService.translate.CompanyLogo} />
                                    <div className="clean-img-thumb"
                                        onClick={e => { e.preventDefault(); e.stopPropagation(); onFileChange(null); !changingImgData && setChangingImgData(true) }}>
                                        <i className="fa fa-times" />
                                    </div>
                                    <div className="edit-img-thumb" onClick={() => { inputRef.current?.click(); !changingImgData && setChangingImgData(true) }}>
                                        <i className="fa fa-edit" />
                                    </div>
                                </div>
                            </div>}

                        {changingImgData ? <div className="d-flex col-auto gap-2">
                            {saving ? <i className="fas fa-spinner-third fa-spin third ms-2" />
                                : <>
                                    {(CompanyService.getCompanyAuth()?.logoUrl || imgSrc) && <button className="btn btn-primary" onClick={onSaveImg}>{TranslationService.translate.SaveChanges}</button>}
                                    {/* <button className="btn btn-primary" onClick={() => { onFileChange(null); }}>{TranslationService.translate.Cancel}</button> */}
                                </>}
                        </div> : null}

                    </div>
                </div>
                <div className="d-flex justify-content-center mt-4">
                    <Collapsable show={Boolean(showDeleteCompany)}>
                        <button className="btn btn-link ps-0" onClick={() => CompanyService.deleteCompany()} >{TranslationService.translate.DeleteCompany}</button>
                        <button className="btn btn-link" onClick={() => CompanyService.resetAll()}>{TranslationService.translate.DeleteCompanyInformation}</button>
                        <button className="btn btn-link" onClick={() => CompanyService.resetIo()}>{TranslationService.translate.DeleteInvoiceAndPaymentsOnly}</button>
                    </Collapsable>
                </div>

            </div>
        </div>
    );
};

export default DataCompanyConfiguration;
